<template>
	<aside id="profile-sidebar" class="sidebar-main hidden-print" :class="{'sidebar-collapsed' : isCollapsed}">
		<button v-if="isCollapsed"
						class="btn btn-sm btn-primary rounded sidebar-toggle"
						type="button"
						@click="toggleCollapse">
			<font-awesome-icon icon="chevron-right" aria-hidden="true"></font-awesome-icon>
			<span class="sr-only">
				{{ $t('MAIN_CONTENT.SidePanel.sideMenuExpandLabel') }}
			</span>
		</button>
		<button v-else
						type="button"
						class="btn btn-sm btn-primary rounded sidebar-toggle"
						@click="toggleCollapse">
			<font-awesome-icon icon="chevron-left" aria-hidden="true"></font-awesome-icon>
			<span class="sr-only">
				{{ $t('MAIN_CONTENT.SidePanel.sideMenuCollapseLabel') }}
			</span>
		</button>

		<div class="mobile-rudder">
			<a href="#" @click="navigateToHomePage()" class="rudder-home">
				<font-awesome-icon icon="home"/> <span class="visible-lg-inline">{{ $t('MAIN_CONTENT.commonContent.homeHeaderLink') }}</span> <span class="sr-only">{{ $t('MAIN_CONTENT.commonContent.currentHeaderLabel') }}</span>
			</a>
			<button type="button"
							class="btn btn-sm btn-primary rounded sidebar-menu-toggle"
							@click="toggleSmallMenu">
				<font-awesome-icon icon="bars" aria-hidden="true"></font-awesome-icon>
				<span class="sr-only">
					{{ $t('MAIN_CONTENT.SidePanel.sideMenuCollapseLabel') }}
				</span>
			</button>
			<help-button linkClass="rudder-help" />
		</div>

		<div class="relative-container" style="width:100%;" :class="{'sidebar-collapsed': isCollapsed, 'media-body': $route.path.includes('account-settings') && !isCollapsed}">
			<section :class="menuClassForSmall" style="width:100%; max-height: 100%; overflow-x: hidden; overflow-y: auto;"
							 v-if="!!selectedStudent || $route.path.includes('account-settings')">
				<h2 v-if="!isCollapsed">
					<span :class="[isCollapsed ? 'sr-only' : '']">
						{{ $t('MAIN_CONTENT.SidePanel.sideMenuMyBackpackHeader') }}
					</span>
				</h2>
				<ul class="sidebar-menu" role="navigation">
					<template v-if="!$route.path.includes('account-settings')">
						<li :class="{'active': $route.path === '/student-information'}"
								@click="closeSmallMenu()"
								v-if="showRole('STUDENT_INFO_FLG')">
							<router-link to="/student-information" :title="$t('MAIN_CONTENT.StudentInformation.sideMenuStudentInformationLink')">
								<div class="sidebar-item-icon">
									<font-awesome-icon icon="book-reader" aria-hidden="true"></font-awesome-icon>
								</div>
								<span :class="[isCollapsed ? 'sr-only' : '']"> {{ $t('MAIN_CONTENT.StudentInformation.sideMenuStudentInformationLink') }} </span>
							</router-link>
						</li>
						<li :class="{'active': $route.path === '/change-student-data'}"
								@click="closeSmallMenu()"
								v-if="showRole('DATA_VERIFICATION_FLG')">
							<router-link to="/change-student-data" :title="$t('MAIN_CONTENT.ChangeStudentData.sideMenuChangeStudentDataLink')">
								<div class="sidebar-item-icon">
									<font-awesome-icon icon="file-signature" aria-hidden="true"></font-awesome-icon>
								</div>
								<span :class="[isCollapsed ? 'sr-only' : '']"> {{ $t('MAIN_CONTENT.ChangeStudentData.sideMenuChangeStudentDataLink') }} </span>
							</router-link>
						</li>
						<li :class="{'active': $route.path === '/student-data-forms'}"
								@click="closeSmallMenu()">
							<router-link to="/student-data-forms" :title="$t('MAIN_CONTENT.StudentDataForms.sideMenuStudentDataFormsLink')">
								<div class="sidebar-item-icon">
									<font-awesome-icon icon="clipboard" aria-hidden="true"></font-awesome-icon>
								</div>
								<span :class="[isCollapsed ? 'sr-only' : '']"> {{ $t('MAIN_CONTENT.StudentDataForms.sideMenuStudentDataFormsLink') }} </span>
							</router-link>
						</li>
						<li :class="{'active': $route.path === '/current-schedule'}"
								@click="closeSmallMenu()"
								v-if="showRole('CURRENT_SCHEDULE_FLG')">
							<router-link :to="{ name: 'CurrentSchedule', params: {}}" :title="$t('MAIN_CONTENT.CurrentSchedule.sideMenuCurrentScheduleLink')">
								<div class="sidebar-item-icon">
									<font-awesome-icon icon="calendar-alt" aria-hidden="true"></font-awesome-icon>
								</div>
								<span :class="[isCollapsed ? 'sr-only' : '']"> {{ $t('MAIN_CONTENT.CurrentSchedule.sideMenuCurrentScheduleLink') }} </span>
							</router-link>
						</li>
						<li :class="{'active': $route.path === '/student-calendar'}"
								@click="closeSmallMenu()">
							<router-link :to="{ name: 'StudentCalendar', params: {}}" :title="$t('MAIN_CONTENT.StudentCalendar.sideMenuStudentCalendarLink')">
								<div class="sidebar-item-icon">
									<font-awesome-icon icon="school" aria-hidden="true"></font-awesome-icon>
								</div>
								<span :class="[isCollapsed ? 'sr-only' : '']"> {{ $t('MAIN_CONTENT.StudentCalendar.sideMenuStudentCalendarLink') }} </span>
							</router-link>
						</li>
						<li :class="{'active': $route.path === '/courses-grades'}"
								@click="closeSmallMenu()"
								v-if="showRole('GRADES_FLG')">
							<router-link to="/courses-grades" :title="$t('MAIN_CONTENT.CoursesAndGradesList.sideMenuCoursesAndGradesLink')">
								<div class="sidebar-item-icon">
									<font-awesome-icon icon="book" aria-hidden="true"></font-awesome-icon>
								</div>
								<span :class="[isCollapsed ? 'sr-only' : '']"> {{ $t('MAIN_CONTENT.CoursesAndGradesList.sideMenuCoursesAndGradesLink') }} </span>
							</router-link>
						</li>
						<li :class="{'active': $route.path === '/assessment-scores'}"
								@click="closeSmallMenu()"
								v-if="showRole('ASSESSMENTS_FLG')">
							<router-link to="/assessment-scores" :title="$t('MAIN_CONTENT.AssessmentScores.sideMenuAssessmentScoresLink')">
								<div class="sidebar-item-icon">
									<font-awesome-icon icon="align-center" aria-hidden="true"></font-awesome-icon>
								</div>
								<span :class="[isCollapsed ? 'sr-only' : '']"> {{ $t('MAIN_CONTENT.AssessmentScores.sideMenuAssessmentScoresLink') }} </span>
							</router-link>
						</li>
						<li :class="{'active': $route.path === '/interactive-scheduler'}"
								@click="closeSmallMenu()"
								v-if="showRole('INTERACTIVE_SCHEDULING_FLG')">
							<router-link to="/interactive-scheduler" :title="$t('MAIN_CONTENT.InteractiveScheduler.sideMenuInteractiveSchedulerLink')">
								<div class="sidebar-item-icon">
									<font-awesome-icon icon="hand-pointer" aria-hidden="true"></font-awesome-icon>
								</div>
								<span :class="[isCollapsed ? 'sr-only' : '']"> {{ $t('MAIN_CONTENT.InteractiveScheduler.sideMenuInteractiveSchedulerLink') }} </span>
							</router-link>
						</li>
						<li :class="{'active': $route.path === '/course-request-forms'}"
								@click="closeSmallMenu()"
								v-if="showRole('COURSE_REQUESTS_FLG') && courseRequestForms.length > 0">
							<router-link to="/course-request-forms" :title="$t('MAIN_CONTENT.SidePanel.sideMenuCourseRequestFormLink')">
								<div class="sidebar-item-icon">
									<font-awesome-icon icon="edit" aria-hidden="true"></font-awesome-icon>
								</div>
								<span :class="[isCollapsed ? 'sr-only' : '']"> {{ $t('MAIN_CONTENT.SidePanel.sideMenuCourseRequestFormLink') }} </span>
							</router-link>
						</li>

						<li :class="{'active': $route.path === '/attendance'}"
								@click="closeSmallMenu()"
								v-if="showRole('ATTENDANCE_FLG')">
							<router-link to="/attendance" :title="$t('MAIN_CONTENT.Attendance.sideMenuAttendanceLink')">
								<div class="sidebar-item-icon">
									<font-awesome-icon icon="list-alt" aria-hidden="true"></font-awesome-icon>
								</div>
								<span :class="[isCollapsed ? 'sr-only' : '']"> {{ $t('MAIN_CONTENT.Attendance.sideMenuAttendanceLink') }} </span>
							</router-link>
						</li>
						<li :class="{'active': $route.path === '/discipline'}"
								@click="closeSmallMenu()"
								v-if="showRole('DISCIPLINE_FLG')">
							<router-link to="/discipline" :title="$t('MAIN_CONTENT.Discipline.sideMenuDisciplineLink')">
								<div class="sidebar-item-icon">
									<font-awesome-icon icon="gavel" aria-hidden="true"></font-awesome-icon>
								</div>
								<span :class="[isCollapsed ? 'sr-only' : '']"> {{ $t('MAIN_CONTENT.Discipline.sideMenuDisciplineLink') }} </span>
							</router-link>
						</li>

						<li :class="{'active': $route.path === '/fees'}"
								@click="closeSmallMenu()"
								v-if="showFeesMenuItem()">
							<template v-if="district.CWP_ALLOW_PAYMENTS_FLG == 'N'">
								<router-link to="/fees" :title="$t('MAIN_CONTENT.Fees.sideMenuFeesLink')">
									<div class="sidebar-item-icon">
										<font-awesome-icon icon="dollar-sign" aria-hidden="true"></font-awesome-icon>
									</div>
									<span :class="[isCollapsed ? 'sr-only' : '']"> {{ $t('MAIN_CONTENT.Fees.sideMenuFeesLink') }} </span>
								</router-link>
							</template>

							<template v-else-if="studentHasSingleEnrollment || webstores.length === 1">
								<a
									target="_blank"
									:href="buildVancoWebstoreUrl(webstores[0])" 
									:title="$t('MAIN_CONTENT.Fees.sideMenuFeesLink')"
									v-if="!!preferences && !!preferences.district && !!preferences.district.WEBSTORE_URL && !!this.preferences.district.WEBSTORE_PROVIDER">
									<div class="sidebar-item-icon">
										<font-awesome-icon icon="dollar-sign" aria-hidden="true"></font-awesome-icon>
									</div>
									<span :class="[isCollapsed ? 'sr-only' : '']"> {{ $t('MAIN_CONTENT.Fees.sideMenuFeesLink') }} </span>
								</a>
							</template> 

							<template v-else>
								<a href="#" @click="setShowWebstoreModal(true)" :title="$t('MAIN_CONTENT.Fees.sideMenuFeesLink')">
									<div class="sidebar-item-icon">
										<font-awesome-icon icon="dollar-sign" aria-hidden="true"></font-awesome-icon>
									</div>
									<span :class="[isCollapsed ? 'sr-only': '']"> {{ $t('MAIN_CONTENT.Fees.sideMenuFeesLink') }} </span>
								</a>
							</template>
						</li>
						<li :class="{'active': $route.path === '/lunch-menus'}"
								@click="closeSmallMenu()"
								v-if="showRole('LUNCH_FLG')">
							<router-link :to="{ name: 'LunchMenus', params: {} }" :title="$t('MAIN_CONTENT.LunchMenus.lunchMenusLabel')">
								<div class="sidebar-item-icon">
									<font-awesome-icon icon="utensils" aria-hidden="true"></font-awesome-icon>
								</div>
								<span :class="[isCollapsed ? 'sr-only' : '']"> {{ $t('MAIN_CONTENT.LunchMenus.lunchMenusLabel') }} </span>
							</router-link>
						</li>
						<!-- TODO: Add conditional logic for the graduation progress -->
						<li :class="{'active': $route.path === '/graduation-progress'}"
								@click="closeSmallMenu()"
								v-if="showRole('GRAD_PROG_FLG')">
							<router-link to="/graduation-progress" :title="$t('MAIN_CONTENT.GraduationProgressReport.sideMenuGraduationProgressLink')">
								<div class="sidebar-item-icon">
									<font-awesome-icon icon="graduation-cap" aria-hidden="true"></font-awesome-icon>
								</div>
								<span :class="[isCollapsed ? 'sr-only' : '']"> {{ $t('MAIN_CONTENT.GraduationProgressReport.sideMenuGraduationProgressLink') }} </span>
							</router-link>
						</li>
						<li :class="{'active': $route.path === '/reports'}" class="treeview" v-if="showRole('REPORTS_FLG')">
							<a href="#" @click="openReportsMenu" :title="$t('MAIN_CONTENT.SidePanel.sideMenuReportsLink')">
								<div class="sidebar-item-icon">
									<font-awesome-icon icon="folder" aria-hidden="true" v-if="!expandReports"></font-awesome-icon>
									<font-awesome-icon icon="folder-open" aria-hidden="true" v-if="expandReports"></font-awesome-icon>
								</div>
								<span :class="[isCollapsed ? 'sr-only': '']"> {{ $t('MAIN_CONTENT.SidePanel.sideMenuReportsLink') }} </span>
							</a>
							<div class="treeview-menu" :class="{'active': expandReports}" v-if="expandReports">
								<div class="inner-menu">
									<h3 @click="expandReportsMenu()" class="pointer">
										<font-awesome-icon icon="chevron-left" aria-hidden="true"></font-awesome-icon>
										{{ $t('MAIN_CONTENT.SidePanel.navPanelReports') }}
									</h3>
									<ul style="list-style-type: unset;">
										<li @click="closeSmallMenu()">
											<router-link to="/report-cards" :title="$t('MAIN_CONTENT.ReportFiles.navPanelReportCards')">
												<div class="sidebar-item-icon">
													<font-awesome-icon icon="file" aria-hidden="true"></font-awesome-icon>
												</div>
												<span>{{ $t('MAIN_CONTENT.ReportFiles.navPanelReportCards') }}</span>
											</router-link>
										</li>
										<li @click="closeSmallMenu()">
											<router-link to="/progress-reports" :title="$t('MAIN_CONTENT.ReportFiles.navPanelProgressReports')">
												<div class="sidebar-item-icon">
													<font-awesome-icon icon="file" aria-hidden="true"></font-awesome-icon>
												</div>
												<span>{{ $t('MAIN_CONTENT.ReportFiles.navPanelProgressReports') }}</span>
											</router-link>
										</li>
										<li @click="closeSmallMenu()">
											<router-link to="/transcript" :title="$t('MAIN_CONTENT.ReportFiles.navPanelTranscript')">
												<div class="sidebar-item-icon">
													<font-awesome-icon icon="file" aria-hidden="true"></font-awesome-icon>
												</div>
												<span>{{ $t('MAIN_CONTENT.ReportFiles.navPanelTranscript') }}</span>
											</router-link>
										</li>
										<li @click="closeSmallMenu()">
											<router-link to="/letters" :title="$t('MAIN_CONTENT.ReportFiles.navPanelLetters')">
												<div class="sidebar-item-icon">
													<font-awesome-icon icon="file" aria-hidden="true"></font-awesome-icon>
												</div>
												<span>{{ $t('MAIN_CONTENT.ReportFiles.navPanelLetters') }}</span>
											</router-link>
										</li>
										<li @click="closeSmallMenu()">
											<router-link to="/file-cabinet" :title="$t('MAIN_CONTENT.FileCabinet.navPanelFileCabinet')">
												<div class="sidebar-item-icon">
													<font-awesome-icon icon="file" aria-hidden="true"></font-awesome-icon>
												</div>
												<span>{{ $t('MAIN_CONTENT.FileCabinet.navPanelFileCabinet') }}</span>
											</router-link>
										</li>
									</ul>
								</div>
							</div>
						</li>
					</template>
					<template v-else>
						<li :class="{'active': $route.path === '/account-settings/general'}"
								@click="closeSmallMenu()"
								class="treeview">
							<router-link to="/account-settings/general" :title="$t('MAIN_CONTENT.General.general')">
								<div class="sidebar-item-icon">
									<font-awesome-icon icon="cog"></font-awesome-icon>
								</div>
								<span :class="[isCollapsed ? 'sr-only': '']"> {{ $t('MAIN_CONTENT.General.general') }}</span>
							</router-link>
						</li>
						<li :class="{'active': $route.path === '/account-settings/devices'}"
								@click="closeSmallMenu()"
								class="treeview">
							<router-link to="/account-settings/devices" :title="$t('MAIN_CONTENT.Devices.devices')">
								<div class="sidebar-item-icon">
									<font-awesome-icon icon="laptop"></font-awesome-icon>
								</div>
								<span :class="[isCollapsed ? 'sr-only': '']"> {{ $t('MAIN_CONTENT.Devices.devices') }}</span>
							</router-link>
						</li>
						<li :class="{'active': $route.path === '/account-settings/students'}"
								@click="closeSmallMenu()"
								class="treeview"
								v-if="role === 'parent'">
							<router-link to="/account-settings/students" :title="$t('MAIN_CONTENT.Students.students')">
								<div class="sidebar-item-icon">
									<font-awesome-icon icon="users"></font-awesome-icon>
								</div>
								<span :class="[isCollapsed ? 'sr-only': '']"> {{ $t('MAIN_CONTENT.Students.students') }}</span>
							</router-link>
						</li>
						<li :class="{'active': $route.path === '/account-settings/notifications'}"
								@click="closeSmallMenu()">
							<router-link to="/account-settings/notifications" :title="$t('MAIN_CONTENT.Notifications.notifications')">
								<div class="sidebar-item-icon">
									<font-awesome-icon icon="bell"></font-awesome-icon>
								</div>
								<span :class="[isCollapsed ? 'sr-only': '']"> {{ $t('MAIN_CONTENT.Notifications.notifications') }}</span>
							</router-link>
						</li>
						<li :class="{'active': $route.path === '/account-settings/third-party-access'}"
								@click="closeSmallMenu()">
							<router-link to="/account-settings/third-party-access" :title="$t('MAIN_CONTENT.thirdPartyAccess.thirdPartyAccess')">
								<div class="sidebar-item-icon">
									<font-awesome-icon icon="key"></font-awesome-icon>
								</div>
								<span :class="[isCollapsed ? 'sr-only': '']"> {{ $t('MAIN_CONTENT.thirdPartyAccess.thirdPartyAccess') }}</span>
							</router-link>
						</li>
					</template>
				</ul>
			</section>
			<students :isCollapsed="isCollapsed" v-if="!selectedStudent && !$route.path.includes('account-settings')">
			</students>
		</div>
	</aside>
</template>

<script>
	import { mapGetters, mapActions, mapMutations } from 'vuex';
	import RulesResource from '../../api/Rules.js';

	import CourseRequest from '../../api/CourseRequest.js';

	import Students from './StudentsList.vue';
	import HelpButton from './HelpButton.vue';

	import Payments from '../../api/Payments';
	import DistrictImage from '../../api/DistrictImage';

	export default {
		name: 'SidePanel',
		data() {
			return {
				isCollapsed:	false,
				expandReports: false,
				expandFees: false,
				showMyStudents: false,
				image: '',
				menuClassForSmall: 'expanded-sm-menu',
				Rules: new RulesResource(),
				courseRequestForms: [],
				linksToShow:	{
					STUDENT: {
						ASSESSMENTS_DETAILS_FLG:			'N',
						ASSESSMENTS_FLG:							'N',
						ATTENDANCE_COMMENTS_FLG:			'N',
						ATTENDANCE_FLG:								'N',
						ATTENDANCE_MINUTES_FLG:				'N',
						BUILDING_NEWS_FLG:						'N',
						COURSE_REQUESTS_FLG:					'N',
						CURRENT_SCHEDULE_FLG:					'N',
						DATA_VERIFICATION_FLG:				'N',
						DISCIPLINE_ACTION_FLG:				'N',
						DISCIPLINE_FLG:								'N',
						DISCIPLINE_INFRACTION_FLG:		'N',
						DISCIPLINE_OFFICIAL_DESC_FLG:	'N',
						FEES_FLG:											'N',
						GRAD_PROG_FLG:								'N',
						GRADES_FLG:										'N',
						REPORTS_FLG:									'N',
						STUDENT_INFO_COUNSELOR_FLG:		'N',
						STUDENT_INFO_FLG:							'N',
						STUDENT_INFO_HOMEROOM_FLG:		'N',
						STUDENT_INFO_LOCKER_COMB_FLG:	'N',
						STUDENT_INFO_LOCKER_FLG:			'N',
						STUDENT_INFO_TRANS_FLG:				'N'
					},
					PARENT: {
						ASSESSMENTS_DETAILS_FLG:			'N',
						ASSESSMENTS_FLG:							'N',
						ATTENDANCE_COMMENTS_FLG:			'N',
						ATTENDANCE_FLG:								'N',
						ATTENDANCE_MINUTES_FLG:				'N',
						BUILDING_NEWS_FLG:						'N',
						COURSE_REQUESTS_FLG:					'N',
						CURRENT_SCHEDULE_FLG:					'N',
						DATA_VERIFICATION_FLG:				'N',
						DISCIPLINE_ACTION_FLG:				'N',
						DISCIPLINE_FLG:								'N',
						DISCIPLINE_INFRACTION_FLG:		'N',
						DISCIPLINE_OFFICIAL_DESC_FLG:	'N',
						FEES_FLG:											'N',
						GRAD_PROG_FLG:								'N',
						GRADES_FLG:										'N',
						REPORTS_FLG:									'N',
						STUDENT_INFO_COUNSELOR_FLG:		'N',
						STUDENT_INFO_FLG:							'N',
						STUDENT_INFO_HOMEROOM_FLG:		'N',
						STUDENT_INFO_LOCKER_COMB_FLG:	'N',
						STUDENT_INFO_LOCKER_FLG:			'N',
						STUDENT_INFO_TRANS_FLG:				'N'
					}
				},
				preferences: {},
				district: {},
			}
		},
		computed: {
			...mapGetters([
				'selectedStudent',
				'selectedStudentPhoto',
				'roleRules',
				'role',
				'userIsAdmin'
			]),
			photoUrl() {
				return this.selectedStudent.PHOTO_URL;
			},
			webstores(){
				let allWebstores = [];
				for( let i=0; i<this.selectedStudent.ENROLLMENT.length; i++){
					let school = this.selectedStudent.ENROLLMENT[i];
					if( (!!this.preferences.schools && !!this.preferences.schools[school.SCHOOL_ID]
							&& !!this.preferences.schools[school.SCHOOL_ID].WEBSTORE_URL
							&& !!this.preferences.schools[school.SCHOOL_ID].WEBSTORE_PROVIDER)
						){
						allWebstores.push(school.SCHOOL_ID);
					} else if( allWebstores.indexOf("DISTRICT") === -1) {
						allWebstores.push("DISTRICT");
					}
				}
				return allWebstores;
			},
			studentHasSingleEnrollment(){
				return this.selectedStudent.ENROLLMENT.length === 1
			},
		},
		components: {
			Students,
			HelpButton,
		},
		watch: {
			selectedStudent: {
				immediate: true,
				handler: function(newValue, oldValue) {
					if(!!newValue) {
						this.getPanelPermissions();
						this.fetchCourseRequestForms();
					}
				},
			},
		},
		methods: {
			...mapActions(['resetSelectedStudentRID']),
			...mapMutations(['setShowWebstoreModal']),
			buildVancoWebstoreUrl(id){
				if( id === 'DISTRICT' ){
					return `https://${this.preferences.district.WEBSTORE_URL}/fee-management?provider=${this.preferences.district.WEBSTORE_PROVIDER}`;
				}

				if( !!this.preferences && !!this.preferences.schools[id] && !!this.preferences.schools[id].WEBSTORE_URL && !!this.preferences.schools[id].WEBSTORE_PROVIDER ){
					return `https://${this.preferences.schools[id].WEBSTORE_URL}/fee-management?provider=${this.preferences.schools[id].WEBSTORE_PROVIDER}`;
				}

				return '';
			},
			getDistrictImage() {
				const self = this;
				const district = new DistrictImage({});
				const promise = district.get();
				promise.then(response => {
					self.$set(self, 'district', district.getResponseData());
				});
			},
			getPreferences(){
				const self = this;
				let payment = new Payments({
					routeParams: {
						service: 'vanco',
						action: 'preferences',
					},
				});

				let paymentPromise = payment.get();
				paymentPromise.then(response =>{
					self.preferences = response.data.data;
				});

				return paymentPromise
			},
			setSelectedStudentAndGoToWelcomeScreen(studentRid) {
				this.$store.dispatch('updateSelectedStudentRID', studentRid);
				this.$router.push({ name: 'HomePage' });
			},
			toggleCollapse() {
				this.isCollapsed = !this.isCollapsed;
				this.expandReports = false;
				this.expandFees = false;
				this.updateClasses();
			},
			updateClasses() {
				let bodyTag = document.getElementsByTagName('body');

				if(this.isCollapsed) {
					bodyTag[0].classList.add('collapsed-sidebar');
				} else {
					bodyTag[0].classList.remove('collapsed-sidebar');
				}
				if( this.menuClassForSmall === 'collapsed-sm-menu' ) {
					this.menuClassForSmall = 'expanded-sm-menu';
				} else {
					this.menuClassForSmall = 'collapsed-sm-menu';
				}
			},
			toggleSmallMenu() {
				this.isCollapsed = !this.isCollapsed;
				this.expandReports = false;
				this.expandFees = false;
				let bodyTag = document.getElementsByTagName('body');
				
				if(this.isCollapsed) {
					bodyTag[0].classList.add('collapsed-sidebar');
				} else {
					bodyTag[0].classList.remove('collapsed-sidebar');
				}

				if( this.menuClassForSmall === 'collapsed-sm-menu' ) {
					this.menuClassForSmall = 'expanded-sm-menu';
				} else {
					this.menuClassForSmall = 'collapsed-sm-menu';
				}
				
			},
			closeSmallMenu() {
				let width = $(window).width();
				let bodyTag = document.getElementsByTagName('body');

				if(width < 1000) {
					this.isCollapsed = true;
					this.menuClassForSmall = 'collapsed-sm-menu';
					bodyTag[0].classList.add('collapsed-sidebar');
				}
			},
			getPanelPermissions() {
				const self = this;

				const Rules = new RulesResource({
					routeParams: {
						role:					self.role
					},
					query: {
						student_rid:	self.selectedStudent.STUDENT_RID
					}
				});

				Rules.get().then(response => {
					if(!!Rules.getResponseData().STUDENT) {
						self.$set(self.linksToShow, 'STUDENT', Rules.getResponseData().STUDENT);
					}

					if(!!Rules.getResponseData().PARENT) {
						self.$set(self.linksToShow, 'PARENT', Rules.getResponseData().PARENT);
					}
				})
				.catch(error => {
					console.log(error);
				});
			},
			showFeesMenuItem(){
				let isVisible = this.showRole('FEES_FLG');

				if( this.district.CWP_ALLOW_PAYMENTS_FLG == 'Y' ){
 					isVisible = this.showRole('FEES_FLG') && this.showRole('FEES_ALLOW_PAYMENTS_FLG') && !this.userIsAdmin;
				}

				return isVisible;
			},
			showRole(flag) {
				// In try/catch so that it will always load rather than break on error(s) for keys not existing
				try {
					return this.roleRules[flag] === 'Y';
				} catch(error) {
					return false;
				}
			},
			navigateToHomePage() {
				if(this.role === 'parent') {
					if(this.$store.state.students.length > 1) {
						this.resetSelectedStudentRID();
					}

					this.$route.name === 'HomePage' ?
						this.$router.push({ name: 'ParentHome' }) :
						this.$router.push({ name: 'HomePage' });
				} else {
					this.$router.push({ name: 'HomePage' });
				}
			},
			fetchCourseRequestForms() {
				const self = this;

				const courseRequest = new CourseRequest({
					route: 'course-request-forms',
					query: {
						student_rid: this.selectedStudent.STUDENT_RID
					}
				});

				const promise = courseRequest.get();

				promise.then(response => {
					self.$set(self, 'courseRequestForms', response.data.data.forms);
				});
			},
			openReportsMenu(){
				this.expandReports = !this.expandReports;
				$(".expanded-sm-menu").animate({
					scrollTop: 1000
				}, 2000);
			},
			expandReportsMenu(){
				this.expandReports = !this.expandReports;
				if ( this.expandReports ){
					this.$nextTick(() => {
						var height = $J('.sidebar-menu li.treeview .treeview-menu').height();
						var top = $J('.sidebar-menu li.treeview .treeview-menu').position().top;
						var bottom = top + height;
						if( window.innerHeight < bottom ){
							$J('.sidebar-menu li.treeview .treeview-menu').css({ top: window.innerHeight - (height + 15) + 'px' });
						}
					});
				}
				
			},
			openFeesMenu(){
				this.expandFees = !this.expandFees;
				$(".expanded-sm-menu").animate({
					scrollTop: 1000
				}, 2000);
			},
			expandFeesMenu(){
				this.expandFees = !this.expandFees;
				if ( this.expandFees ){
					this.$nextTick(() => {
						var height = $J('.sidebar-menu li.treeview .treeview-menu.fees-treeview-menu').height();
						var top = $J('.sidebar-menu li.treeview .treeview-menu.fees-treeview-menu').position().top;
						var bottom = top + height;
						if( window.innerHeight < bottom ){
							$J('.sidebar-menu li.treeview .treeview-menu.fees-treeview-menu').css({ top: window.innerHeight - (height + 15) + 'px' });
						}
					});
				}
				
			}
		},
		created() {
			this.fetchCourseRequestForms();
			this.getDistrictImage();
			this.getPreferences();
			if(window.innerWidth < 1000) {
				this.isCollapsed = true;
				this.menuClassForSmall = 'collapsed-sm-menu';
				document.getElementsByTagName('body')[0].classList.add('collapsed-sidebar');
			}
		},
	}
</script>

<style scoped>
	::-webkit-scrollbar {
		width: 2px;
	}

	::-webkit-scrollbar-track {
		-webkit-box-shadow: inset 0 0 6px rgba(0,0,0,0.3); 
		border-radius: 10px;
	}

	::-webkit-scrollbar-thumb {
		border-radius: 10px;
		-webkit-box-shadow: inset 0 0 6px rgba(0,0,0,0.5); 
	}
</style>
